import { useState } from 'react';
import { CalendarRange, ChevronLeft, ChevronRight } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import formatMonthKey from '../helpers/formatMonthKey';
import formatPeriodLabel from '../helpers/formatPeriodLabel';
import getIsPeriodCurrentMonth from '../helpers/getIsPeriodCurrentMonth';
import shiftMonthKey from '../helpers/shiftMonthKey';
import buildMonthPickerGrid from './helpers/buildMonthPickerGrid';
import getCanNavigatePickerYear from './helpers/getCanNavigatePickerYear';
import type { PeriodPickerProps } from './types';
import {
    FilterShell,
    MonthCellButton,
    MonthGrid,
    PeriodLabelButton,
    PeriodStepButton,
    PopoverPanel,
    YearHeader,
    YearLabel,
    YearStepButton,
} from './styled';

const MAX_LOOKBACK_MONTHS = 24;

function PeriodPicker({ month, onMonthChange }: PeriodPickerProps) {
    const now = new Date();
    const currentMonth = formatMonthKey({ date: now });
    const earliestMonth = shiftMonthKey({ month: currentMonth, delta: -MAX_LOOKBACK_MONTHS });
    const isCurrentMonth = getIsPeriodCurrentMonth({ month, now });
    const canGoPrevious = month > earliestMonth;
    const selectedYear = Number(month.slice(0, 4));

    const [isOpen, setIsOpen] = useState(false);
    const [viewYear, setViewYear] = useState(selectedYear);

    const { canGoPreviousYear, canGoNextYear } = getCanNavigatePickerYear({
        viewYear,
        currentMonth,
        earliestMonth,
    });

    const monthCells = buildMonthPickerGrid({
        year: viewYear,
        selectedMonth: month,
        currentMonth,
        earliestMonth,
    });

    const handleOpenChange = (open: boolean) => {
        if (open) {
            setViewYear(selectedYear);
        }
        setIsOpen(open);
    };

    const handleSelectMonth = ({ monthKey }: { monthKey: string }) => {
        onMonthChange({ month: monthKey });
        setIsOpen(false);
    };

    return (
        <FilterShell>
            <PeriodStepButton
                type="button"
                aria-label="Previous month"
                disabled={!canGoPrevious}
                onClick={() => onMonthChange({ month: shiftMonthKey({ month, delta: -1 }) })}
            >
                <ChevronLeft size={14} />
            </PeriodStepButton>

            <Popover open={isOpen} onOpenChange={handleOpenChange}>
                <PopoverTrigger asChild>
                    <PeriodLabelButton type="button" aria-label="Choose month">
                        <CalendarRange size={14} />
                        {formatPeriodLabel({ month })}
                    </PeriodLabelButton>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-[220px] p-0">
                    <PopoverPanel>
                        <YearHeader>
                            <YearStepButton
                                type="button"
                                aria-label="Previous year"
                                disabled={!canGoPreviousYear}
                                onClick={() => setViewYear(currentViewYear => currentViewYear - 1)}
                            >
                                <ChevronLeft size={14} />
                            </YearStepButton>
                            <YearLabel>{viewYear}</YearLabel>
                            <YearStepButton
                                type="button"
                                aria-label="Next year"
                                disabled={!canGoNextYear}
                                onClick={() => setViewYear(currentViewYear => currentViewYear + 1)}
                            >
                                <ChevronRight size={14} />
                            </YearStepButton>
                        </YearHeader>
                        <MonthGrid>
                            {monthCells.map(cell => (
                                <MonthCellButton
                                    key={cell.monthKey}
                                    type="button"
                                    $isSelected={cell.isSelected}
                                    data-selected={cell.isSelected ? 'true' : 'false'}
                                    disabled={cell.isDisabled}
                                    aria-label={formatPeriodLabel({ month: cell.monthKey })}
                                    aria-current={cell.isSelected ? 'true' : undefined}
                                    onClick={() => handleSelectMonth({ monthKey: cell.monthKey })}
                                >
                                    {cell.label}
                                </MonthCellButton>
                            ))}
                        </MonthGrid>
                    </PopoverPanel>
                </PopoverContent>
            </Popover>

            <PeriodStepButton
                type="button"
                aria-label="Next month"
                disabled={isCurrentMonth}
                onClick={() => onMonthChange({ month: shiftMonthKey({ month, delta: 1 }) })}
            >
                <ChevronRight size={14} />
            </PeriodStepButton>
        </FilterShell>
    );
}

export default PeriodPicker;
